/****
 * API Route Constants
 * @author Karen Yunuen Pozos Arias
 *
 * This file centralizes the definition of service names and versioned base paths
 * for backend REST endpoints. It helps prevent string duplication, enables easy
 * refactoring, and keeps route construction consistent across the application.
 * The constants defined here are typically used by API clients such as Axios,
 * RTK Query, or Redux Thunks when performing HTTP requests.
 * ***/

export const FILE_UPLOAD_SERVICE = {
    name: 'file-upload',
    baseV1: `file-upload/v1/`,
    zipDownload: `file-upload/v1/zip-download`,
    zipDownloadByUserId: `file-upload/v1/zip-download/userId/`,
    download: `file-upload/v1/download`,
};

export const EVENT_SERVICE = {
    fileCreate: `event-service/setting/${FILE_UPLOAD_SERVICE.name}/create`,
    fileDeleteByIds: `event-service/setting/${FILE_UPLOAD_SERVICE.name}/delete-by-ids`,
    createNormalEvent: `event-service/v1/create`,
    getEventDetail: `event-service/v1/get-by-id/`,
    deleteUserInvitation: `event-service/delete-user-invitation-by-invitation-id/`,
    cancelEvent: `event-service/cancel-event/`,
    cancelEventByList: `event-service/cancel-event/by-list`,
    companyRestrictions: `event-service/restriction/get`,
    deleteEvents: `event-service/delete-by-ids`,
    validateEvent: (id, option) => `event-service/validate-event/${id}/validation/${option}`,
    deleteUserInvitationByIds: `event-service/delete-user-invitation-by-user-list/by-event-id/`,
    getFreeShedules: `event-service/v1/reservation/get-free-schedules/by-zone`,
    getEventSetting: `event-service/setting/get`,
    updateEventSetting: `event-service/setting/update`,
    getUserInvitations: (id, userTypeId) => `event-service/v1/web-app/get-all-user-invitations/${id}/${userTypeId}`,
    groupInvitationsByEventId: `event-service/v1/web-app/count-user-invitations/`,
    getEventApprovalComments: `event-service/v1/event-approval/comments/`,
    createUserInvitationEvent: `event-service/v1/create-user-invitations`,
    getPublicEventDetail: `event-service/public/v1/details/`,
    publicCreateUserInvitations: `event-service/public/v1/create-user-invitations`,
    publicVerifyEmail: `event-service/public/v1/verify-email`,
    createWhiteListReservationAutomatic: `event-service/v1/attendance/upload`,
    createWhiteListReservationManual: `event-service/v1/attendance/create`,
    getUserReservationSummary: `event-service/v1/summary/`,
    countEventWhiteList: `event-service/count-attendances/`,
    cancelInvitation : 'event-service/v1/remove-user-invitations',
};

export const EQUIPMENT_SERVICE = {
    equipmentCheckInOut: 'equipment-service/v1/access-log/create',
    checkInLog: (id) => `equipment-service/v1/access-log/${id}/check-in/log`,
    checkOutLog: (id) => `equipment-service/v1/access-log/${id}/check-out/log`,
    contractorEmployeeDetail: (id) => `equipment-service/v1/access-log/${id}/contractor-employee/detail`,
    materialDetail: (id) => `equipment-service/v1/access-log/${id}/material/detail`,
    createEquipment: `equipment-service/v1/create`,
    createDeliveryAsset: `equipment-service/v1/delivery/create-asset`,
    getTrackByDeliveryId: `equipment-service/v1/tracker/get-all-by-delivery-id/`,
    uploadActivityTracker: `equipment-service/v1/tracker/create-asset`,
    getEquipmentDetailsById: `equipment-service/v1/get-by-id/`,
    updateEquipment: `equipment-service/v1/update`,
    getCategoryAndSubcategory: `equipment-service/v1/category/simple/user`,
    createCategoryAndSubcategory: `equipment-service/v1/category/create`,
    createEquipmentImage: `equipment-service/v1/create-asset`,

    createDelivery: `equipment-service/v1/delivery/create`,
    getDeliveryById: `equipment-service/v1/delivery/get-by-id/`,
    updatedelivery: `equipment-service/v1/delivery/update`,
    createActivityTracker: `equipment-service/v1/tracker/create`,
    getEquipmentList: `equipment-service/v1/simple/contractor`,
};

export const LOG_SERVICE = {
    getAllVehicleAccessLogByVehicleId: `log-service/vehicle-access/get-all-pageable/vehicle-id/`,
    updateAccessComment: `log-service/v1/app/user-access/comment-access`,
    getAllUserAccessLogByUserId: `log-service/user-access/get-all-pageable/user-id/`,
    getVehicleAccessList: `log-service/vehicle-access/get-all-pageable`,
    getUserAccessList: `log-service/user-access/get-all-pageable`,
    getWorkOffice: `log-service/home-office/get`,
    checkInWorkOffice: `log-service/home-office/check-in`,
    checkOutWorkOffice: `log-service/home-office/check-out`,
    getAllListOfAccess: (from, to) => `log-service/user-access/get-all-pageable/from/${from}/to/${to}`,
    getCrudLogList: `log-service/crud/get-all-pageable`,
    getLoginLogList: `log-service/log-in/get-all-pageable`,
};

export const DOCUMENTATION_APPROVAL_SERVICE = {
    baseDocumentationUser: `documentation-approval-service/documentation-user/`,
    baseDocumentationVehicle: `documentation-approval-service/documentation-vehicle/`,
    createDocumentationApprovalFlow: `documentation-approval-service/create`,
    deleteDocumentationApprovalFlowByIds: `documentation-approval-service/delete-by-ids`,
    updateDocumentationApprovalFlow: `documentation-approval-service/update`,
    getDocumentationRequestByUserTypeIdAndProviderId: `documentation-approval-service/documentation-user/get-documentation-request/`,
    getDocumentationUserByUserId: `documentation-approval-service/documentation-user/user/`,
    documentationUserUpdateStatusById: `documentation-approval-service/documentation-user/status/by-id`,
    documentationUserUpdateStatusByUserIds: `documentation-approval-service/documentation-user/status/by-users-ids`,
    documentationVehicleUpdateStatusById: `documentation-approval-service/documentation-vehicle/status/by-id`,
    documentationVehicleUpdateStatusByVehicleIds: `documentation-approval-service/documentation-vehicle/status/by-vehicles-ids`,
    getPeriodRange: `documentation-approval-service/documentation-period/range/user-type/`,
    getUploadDocumentApprovalPeriod: `documentation-approval-service/documentation-period/upload/user-type/`,
    getDocumentationApprovalFlowByOptions: `documentation-approval-service/get-by-options`,
    getDocumentApprovalFlowById: `documentation-approval-service/get-by-id/`,
    getActiveDocumentUserList: `/get-active-levels`, // documentation-approval-service/documentation-user/{userTypeId}/{userId}/{profile}/get-active-levels
    getActiveDocumentVehicleList: `/get-active-levels`, // documentation-approval-service/documentation-vehicle/{userTypeId}/{vehicleId}/get-active-levels
    getDocumentationUserFlowByUserId: `documentation-approval-service/documentation-user/get-by-user-id/`,
    getDocumentationVehicleFlowByUserId: `documentation-approval-service/documentation-vehicle/get-by-user-id/`,
    getDocumentationVehicleByVehicleId: `documentation-approval-service/documentation-vehicle/vehicle/`,
    getDocumentationApprovalFlowByVehicleOptions: `documentation-approval-service/get-by-vehicle-options`,
    createDocumentationApprovalUserLevel: `documentation-approval-service/documentation-user/create-by-list`,
    createDocumentationApprovalVehicleLevel: `documentation-approval-service/documentation-vehicle/create-by-list`,
};

export const ATTENDANCE_SERVICE = {
    // color
    getAttendanceColor: `attendance-service/v1/color/get`,
    updateAttendanceColor: `attendance-service/v1/color/update`,
    resetToDefaultAttendanceColor: `attendance-service/v1/color/reset`,
    // cycle
    getAttendanceCycleConfig: `attendance-service/v1/cycle/get`,
    updateAttendanceCycleConfig: `attendance-service/v1/cycle/update`,
    // department
    getAttendanceDepartments: `attendance-service/v1/department/get-all`,
    // geoFence
    getGeofenceById: `attendance-service/v1/geofences/`,
    createGeofence: `attendance-service/v1/geofences/create`,
    updateGeofenceStatus: `attendance-service/v1/geofences/update-status`,
    updateGeofence: `attendance-service/v1/geofences/update`,
    updateGeofenceHierarchyLevel: `attendance-service/v1/geofences/update-hierarchy-Level`,
    getGeofenceList: `attendance-service/v1/geofences/get-all`,
    getGeofenceAvailableDevices: `attendance-service/v1/geofences/available-devices`,
    getGeofenceConfigSetting: `attendance-service/v1/geofence-config/geofence-id/`,

    getCustomAccessByShiftId: `attendance-service/get-all-pageable-custom-access/by-shift-id/`,
    getColorAttendance: `attendance-service/v1/color/get`,
    createAttendanceDepartment: `attendance-service/v1/department/create`,
    updateAttendanceDepartment: `attendance-service/v1/department/update`,
    getAllLocationsByDepartment: `attendance-service/v1/get/locations`,
    getAllLastEntryByUser: (id, total) => `attendance-service/v1/get/${id}/last/${total}`,
    createAttendanceShift: `attendance-service/v1/shift/create`,
    updateAttendanceShift: `attendance-service/v1/shift/update`,
    getAttendanceShiftById: `attendance-service/v1/shift/by-id/`,
    getShiftResourceAssigned: `attendance-service/v1/resource-mode/simple/assigned`,
    getShiftResourceNotAssigned: `attendance-service/v1/resource-mode/simple/`,
    //AttendanceDepartmentSupervisor
    simpleSearchDepartmentSupervisor: `attendance-service/v1/supervisor/simple`,
    isAttendanceSupervisor: `attendance-service/v1/supervisor/exist`,
    getAttendanceDepartmentAssigned: `attendance-service/v1/supervisor/department/assigned`
};

export const EMAIL_SETTINGS_SERVICE = {
    // user-email
    getAllOnlyUserData: `email-settings-service/user-email/get-all/only-user-data`,
    updateUserEmailByList: `email-settings-service/user-email/update-list`,
    // email-settings
    updateEmailSettingAttendanceManage: `email-settings-service/update`,
    getEmailSetting: `email-settings-service/get`,
    getAllSelectedDevice: `email-settings-service/device-email/get-all/only-device-data`,
    addDeviceToListPayroll: `email-settings-service/device-email/create/by-device-id/`,
    deleteDeviceToListPayroll: `email-settings-service/device-email/delete-by-device-id/`,
    getAllSelectedEmployees: `email-settings-service/user-email/get-all-pageable/only-user-data`,
    addEmployeesToListPayroll: `email-settings-service/user-email/create/by-user-id/`,
    deleteEmployeesToListPayroll: `email-settings-service/user-email/delete-by-user-id/`,
    updateEmailSetting: `email-settings-service/update/email-settings`,
    removeDeviceAttendance: `email-settings-service/device-email/delete-list`,
    removeEmployeeAttendance: `email-settings-service/user-email/delete-list`,
};

export const FOODIE_MENU_SERVICE = {
    checkOutOrderKitchen: `foodie-menu-service/v2/orders/check-out/`,
    getActiveExtraOrders: `foodie-menu-service/v2/extra/orders/search-active`,
    getAllMealTimes: `foodie-menu-service/v1/meal-times/get-all`,
    getCustomAccessByShiftIdFoodie: `foodie-menu-service/v1/get-all-pageable-custom-access/by-shift-id/`,
    getCafeteriaById: `foodie-menu-service/v1/cafeterias/by-id/`,
    createCafeteria: `foodie-menu-service/v1/cafeterias/create`,
    updateCafeteria: `foodie-menu-service/v1/cafeterias/update`,
    getAvailableDevicesByModeIds: `foodie-menu-service/v1/cafeterias/devices/available`,
    getDepartmentsByCafeteriaId: (id) => `foodie-menu-service/v1/cafeterias/${id}/departments/download`,
    getUsersByCafeteriaId: (id) => `foodie-menu-service/v1/cafeterias/${id}/users/download`,
    getDietTypes: `foodie-menu-service/v1/diet-types/get-all`,
    getAllAllergens: `foodie-menu-service/v1/allergens/get-all`,
    getAllIngredients: `foodie-menu-service/v1/ingredients/get-all`,
    getDishById: `foodie-menu-service/v1/items/`,
    createDish: `foodie-menu-service/v1/items/create`,
    updateDish: `foodie-menu-service/v1/items/update`,
    createDishImage: `foodie-menu-service/v1/items/image/create`,
    getDishImageByDishId: `foodie-menu-service/v1/foodie_item/image/by-id/`,
    createExtraMeal: `foodie-menu-service/v2/orders/create`,
    getExtraOrdersByAdmin: `foodie-menu-service/v2/extra/orders/search`,
    updateExtraOrders: `foodie-menu-service/v2/orders/update`,
    retrievesAQrCodeBody: `foodie-menu-service/v1/extra/orders/qr-code/`,
    getSummaryById: `foodie-menu-service/v2/summary/get-by-id/`,
    getStatusByUserIdActive: `foodie-menu-service/v2/extra/orders/active-statuses/`,
    getStatusByUserIdHistory: `foodie-menu-service/v1/extra/orders/history-statuses/`,
    getQr: `foodie-menu-service/v2/orders/qr-code/`,
    getFoodieApprovalByRequestMealId: `foodie-menu-service/v2/foodie-approval/user-menu-id/`,
    getMeasurementUnits: `foodie-menu-service/v1/measurement-units/get-all`,
    getIngredientById: `foodie-menu-service/v1/ingredients/by-id/`,
    createIngredient: `foodie-menu-service/v1/ingredients/create`,
    updateIngredient: `foodie-menu-service/v1/ingredients/update`,
    createIngredientImage: `foodie-menu-service/v1/ingredients/image/create`,
    getIngredientImageByIngredientId: `foodie-menu-service/v1/ingredients/image/by-id/`,
    deleteAllergenById: (foodieItemId, allergenId) =>
        `foodie-menu-service/v1/items/${foodieItemId}/remove-allergen/${allergenId}`,
    deleteDietTypeById: (foodieItemId, dietTypeId) =>
        `foodie-menu-service/v1/items/${foodieItemId}/remove-diet-type/${dietTypeId}`,
    deleteIngredientById: (foodieItemId, ingredientId) =>
        `foodie-menu-service/v1/items/${foodieItemId}/remove-ingredient/${ingredientId}`,
    getMealTimeById: `foodie-menu-service/v1/meal-times/by-id/`,
    createMealTime: `foodie-menu-service/v1/meal-times/create`,
    updateMealTime: `foodie-menu-service/v1/meal-times/update`,
    getAllMeals: `foodie-menu-service/v1/meals/get-all/`,
    getAllCourseType: `foodie-menu-service/v1/courses/get-all`,
    getAllItem: `foodie-menu-service/v1/items/get-all`,
    getMenuById: `foodie-menu-service/v1/menus/by-id/`,
    createMenu: `foodie-menu-service/v1/menus/create`,
    updateMenu: `foodie-menu-service/v1/menus/update`,
    getMenuByCafeteriaIdServingDate: `foodie-menu-service/v1/menus/by-cafeteria-id/`,
    deleteDepartmentById: (foodieMenuId, departmentId) =>
        `foodie-menu-service/v1/menus/${foodieMenuId}/remove-department/${departmentId}`,
    deleteMealTimeById: (foodieMenuId, mealTimeId) =>
        `foodie-menu-service/v1/menus/${foodieMenuId}/remove-meal-time/${mealTimeId}`,
    deleteUserById: (foodieMenuId, userId) => `foodie-menu-service/v1/menus/${foodieMenuId}/remove-user/${userId}`,
    getAllMealForecast: `foodie-menu-service/v2/forecast/get`,
    getAppetizer: `/foodie-menu-service/v1/search`,
    saveOrder: `/foodie-menu-service/v2/orders/create`,
    saveRequestMeal: `/foodie-menu-service/v2/orders/meals`,
    getTotalMealsKpisGrid:`/foodie-menu-service/v2/orders/dashboard/kpi-stats/`,
    getMealTimeStats: `/foodie-menu-service/v2/orders/dashboard/meal-time-stats/`,
    getAllCafeterias: `foodie-menu-service/v1/cafeterias/get-all`,
    getAllCafeteriasByUserId: `foodie-menu-service/v1/cafeterias/get-all/by-user`,
    getShiftById: `foodie-menu-service/v1/shifts/by-id/`,
    createShift: `foodie-menu-service/v1/shifts/create`,
    updateShift: `foodie-menu-service/v1/shifts/update`,
    getFoodieShiftResourceAssigned: `foodie-menu-service/v1/shifts/resource-mode/simple/assigned`,
    getFoodieShiftResourceNotAssigned: `foodie-menu-service/v1/shifts/resource-mode/simple/`,
};

export const ZONE_SERVICE = {
    getZoneSummaryLevel: `zone-service/v2/get-zone-summary-level/by-father-id`,
    getFullTreeSummary: `zone-service/v2/get-full-tree-summary`,
    getAllZoneTree: `zone-service/get-tree`,
    getWorkStations: `zone-service/get-all`,
    getAllCommonArea: `zone-service/v1/common-area/get-all`,
    getListFatherZones: `zone-service/get-zones-and-children`,
    createFatherZone: `zone-service/create-father`,
    createChildZone: `zone-service/create-child`,
    zoneDetailFatherAndChild: 'zone-service/get-zone-father-and-children/by-zone-id/',
    zoneDetailSummary: 'zone-service/get-zone-summary-father-and-children/by-zone-id/',
    zoneDetailChildV1: `zone-service/v1/get-by-id/`,
    zoneDetailChild: `zone-service/get-by-id/`,
    zoneDetailAuthorizedEmployee: `zone-service/get-all-pageable/authorized-employees/`,
    zoneDetailAuthorizedEmployeeNoPagination: `zone-service/get-all/authorized-employees/`,
    zoneDetailListDevice: `zone-service/zone-plane/get-all/by-zone-id/`,
    updateZone: `zone-service/v1/update`,
    createCommonAreaZone: `zone-service/common-area/create`,
    updateCommonAreaZone: `zone-service/common-area/update`,
    deleteZoneUser: `zone-service/user-zone/delete-by-user-id-list/by-zone-id/`,
    createUserZoneList: `zone-service/user-zone/create-list`,
    getListZoneMap: `zone-service/zone-plane/get-all/by-zone-id/`,
    createZonePlane: `zone-service/zone-plane/create`,
};

export const HID_SERVICE = {
    getAcrOutputsAvailableBySio: `hid-service/v1/acrs/outputs-by-sio/`,
    getAcrInputsBySioAndFunction: (hidSioId, inputFunction) => `hid-service/v2/acrs/inputs-by-sio/${hidSioId}/by-function/${inputFunction}`,
    getInputTypeConnections: `hid-service/v1/input/type-connections`,
    getInputFunctions: `hid-service/v1/input/functions`,
    createHidRecord: `hid-service/v1/create`,
    getSio: `/hid-service/v1/sio/get-free`,
    createHidSioRecord: `hid-service/v1/sio/create`,
    createHidInput: `hid-service/v1/input/create`,
    createHidOutput: `hid-service/v1/output/create`,
    createFormat: `hid-service/v1/format/create`,
    createHidReader: `hid-service/v1/reader/create`,
    createHidAcr: `hid-service/v1/acrs/create`,
    updateHidRecord: `hid-service/v1/update`,
    connectWithHid: `hid-service/v1/connect/`,
    sendInformationSetting: `hid-service/v1/settings/`,
    resetHid: `hid-service/v1/reset/`,
    getAllSiosByHidId: `hid-service/v1/sio/get/by-hid-id/`,
    getHidRecordDetails: `/hid-service/v1/get-by-id/`,
    getAcrReadersAvailableBySio: 'hid-service/v1/acrs/readers-by-sio/',
    openTheDoor: `hid-service/v1/open-the-door/`,
    linkingMode: `hid-service/v1/acrs/set-linking-mode/`,
};

export const CONTRACTOR_EMPLOYEE_SERVICE = {
    updateContractorEmployee: `contractor-employee-service/v1/update`,
    createContractorEmployee: `contractor-employee-service/v1/create`,
    createlistofContractorEmployeeContract: `contractor-employee-service/contract/create-list`,
    getEmployeOnContractById: `contractor-employee-service/contract/get-all-pageable-employees/by-contract-id/`,
    getlistofcontractorEmployees: `contractor-employee-service/get-all/by-contractor-id/`,
    getlistofEmployeWorkOnContract: `contractor-employee-service/contract/get-all-employees/by-contract-id/`,
    addContractorsEmployee: `contractor-employee-service/create`,
    getContractorEmployeeDetail: `contractor-employee-service/company/get-by-user-id/`,
    getContractorInfoById: `contractor-employee-service/v1/get-by-user-id/`,
    deleteEmployeeByContractAndUserList: (contractId) =>
        `contractor-employee-service/contract/delete-by-contract-id/${contractId}/by-user-list`,
    createListContractorEmployee: `contractor-employee-service/v1/create-list`,
};

export const SUPPLIER_EMPLOYEE_SERVICE = {
    updateSupplierEmployee: `supplier-employee-service/v1/update`,
    createSupplierEmployee: `supplier-employee-service/v1/create`,
    createListSupplierEmployee: `supplier-employee-service/v1/create-list`,
    getSupplierInfoById: `supplier-employee-service/v1/get-by-user-id/`,
    getProviderEmployeeDetail: `supplier-employee-service/company/get-by-user-id/`,
};

export const CONTRACTOR_SERVICE = {
    updateContractor: `contractor-service/v1/update`,
    getByUserId: `contractor-service/v1/get-by-user-id/`,
    getContractorsByUserId: `contractor-service/get-by-user-id/`,
    getAllContractors: `contractor-service/get-all`,
    getAllEmployeeContractors: `contractor-service/get-all-pageable`,
    getEmployeeContractorById: `contractor-service/get-by-id/`,
    createEmployeeContractor: `contractor-service/v1/create`,
    getContractorById: `contractor-service/v1/get-by-id/`,
    createListContractor: `contractor-service/create-list`,
    getContractorRestriction: `contractor-service/setting/`,
    updateContractorRestriction: `contractor-service/setting/update`,
};

export const CONTRACT_SERVICE = {
    getContractByIdV2: `contract-service/v2/get-by-id/`,
    createContract: `contract-service/create`,
    createContractV1: `contract-service/v1/create`,
    getAllEmployeeContracts: `contract-service/company/get-all-pageable/`,
    upDateContract: `contract-service/update`,
    updateContractV1: `contract-service/v1/update`,
};

export const SUPPLIER_SERVICE = {
    updateSupplier: `supplier-service/v1/update`,
    getEmployeeProviderLists: `supplier-service/get-all`,
    createEmployeeSupplier: `supplier-service/v1/create`,
    getEmployeeSupplierByItId: `supplier-service/v1/get-by-id/`,
    createListSupplier: `supplier-service/create-list`,
    getProvidersByUserId: `supplier-service/get-by-user-id/`,
    getSupplierRestriction: `supplier-service/setting/get`,
    updateSupplierRestriction: `supplier-service/setting/update`,
};

export const USER_SERVICE = {
    getUserPreferencesByUserId: `user-service/preferences/get/by-user-id/`,
    updateUserPreferences: `user-service/preferences/update`,
    createUserPreferences: `user-service/preferences/create`,
    userDocumentsByUserId: `user-service/user-document/v1/by-user-id/`,
    deleteUserDocument: `user-service/user-document/v1/`,
    updateUserProfile: `user-service/v1/app/update`,
    getUserFullInformation: `user-service/v1/app/full-information/get-by-id/`,
    qrCodeByEmail: `user-service/send-qr-code/by-id/`,
    resetByAttemps: `user-service/v1/reset-validation-attempts/by-id/`,
    resetUnlockedAccount: `user-service/v1/unlocked-account/by-id/`,
    hasSelfi: `user-service/user-image/check-selfie/by-user-id/`,
    createImgObj: `user-service/user-image/create`,
    publicCreateUserImage: `user-service/public/user-image/create`,
    getSelfie: `user-service/user-image/get-selfie/by-user-id/`,
    getSelfies: `user-service/user-image/get-selfies/by-user-ids`,
    getAllUserByUserTypeId: `user-service/get-all/list-user-id`,
    validateIdentity: `user-service/validate-identity`,
    updateUserStatus: `user-service/update-status`,
    getUserDetailByUserId: `user-service/get-by-id/`,
    searchByEmail: `user-service/get-by-email/`,
    searchByPhone: `user-service/get-by-phone-number/`,
    getUserById: `user-service/get-by-user-id/`,
    getSingleProvider: `user-service/get-by-id/`,
    unlinkUserDevice: `user-service/unlink-device/by-id/`,
    getSingleUserProvider: `user-service/get-by-id/`,
    getUserRestriction: `user-service/restriction/get`,
    updateUserRestriction: `user-service/restriction/update`,
    getAllUserFiles: `user-service/user-file/get-all/by-user-id/`,
    publicCreateUserFile: `user-service/public/user-file/create`,
    publicDeleteUserFile: `user-service/public/user-file/delete/`,
    publicDeleteUserImageByUserAndAccessMethod: `user-service/public/user-image/delete/`,
    publicGetQrCodeByUserId: `user-service/public/qr-code/`,
    getQrCode: `user-service/v1/app/qr-code`,
    publicVerifyIdentity: `user-service/public/verify-identity/`,
};
export const ASSETS_SERVICE = {
    hardDeleteRecordsByTableAndIds: 'assets-service/hard-delete-by-ids/by-table/',
    deleteRelationshipsWithDevices: "/assets-service/delete-by-ids/by-table/", //options: device_department OR device_custom_access
    deleteItemsApi: `assets-service/delete-by-ids/by-table/`,
    getAllMedia: `assets-service/media/get-all`,
    getSoftDeletedRecordsByTable: `assets-service/backup/soft-deleted/by-option/`,
    restoreRecords: `assets-service/restore/records-of-table/`,
    getFilterForEntitiesWithSoftDelete: `assets-service/backup/soft-deleted-restore/models`,
    getAllEntities: `assets-service/back-up/restore/soft-deleted/get-all-entities`,
    getRecordInTimePeriod: (table, from, to) =>
        `assets-service/back-up/restore/get-pageable-records-of-table/${table}/from/${from}/to/${to}`,
    getInformationById: (id, table) => `assets-service/get-by-id/${id}/get-by-table/${table}`,
    restoreTableInformation: `assets-service/back-up/restore/records-of-table/`,
    createDataBaseBackUp: `assets-service/back-up-database?file_name=`,
    fullDBRestore: `assets-service/database-restore`,
    getAllEmployeeFilter: `assets-service/contractor-employee/get-filters`,
    getEventFilters: `assets-service/event/get-filters`,
    getDocumentRestriction: `assets-service/media/get-all`,
};

export const VERIDAS_SERVICE = {
    getServiceById: 'veridas-service/v1/get/',
    getIdentityValidationType: `veridas-service/v1/public/get-identity-validation-type`,
};

export const APPROVAL_TRACKER_SERVICE = {
    approveMultipleRequestMeals: 'approval-tracker-service/v1/approve',
    getApprovalTrackerProgress: (approvalFlowId, subjectId, module) =>
        `approval-tracker-service/v1/progress/${approvalFlowId}/subject/${subjectId}/module/${module}`,
    updateApprovalTrackerFlow: `approval-tracker-service/v1/flow-decision`,
    markFilesAsRead: `approval-tracker-service/v1/notifications/mark-as-read`,
};

export const FOODIE_MEAL_ENTRY_SERVICE = {
    publishDailyKitchenAccess: '/app/daily-kitchen-access',
    topicDailyKitchenAccess: '/topic/daily_kitchen_access',
};

export const DOCUMENT_SERVICE = {
    contractorVehicleDoc: `document-service/contractor-vehicle/approve-document`,
    supplierVehicleDoc: `document-service/supplier-vehicle/approve-document`,
    createVehicles: `document-service/external-vehicle/create`,
    getCompanyVehicles: `document-service/external-vehicle/get-all/for-company`,
    documentListing: `document-service/employee/get-all/by-user-id/`,
    approveDocs: `document-service/approve-employee-document`,
    createToExternalVehicle: `document-service/contractor-vehicle/create`,
    setToExternalVehicle: `document-service/contractor-vehicle/set-comment`,
    setCommentaToExternalDoc: `document-service/external/set-comment`,
    getAllContractorDocuments: `document-service/contractor/get-all/by-user-id/`,
    createContractorDocValue: `document-service/contractor/create`,
    setContractorDocValue: `document-service/contractor/set-comment`,
    createEmployeeDoc: `document-service/employee-company/create`,
    updateEmployeeDoc: `document-service/employee-company/update`,
    getEmployeeDocById: `document-service/employee-company/get-by-id/`,
    createContractorDoc: `document-service/contractor-company/create`,
    updateContractorDoc: `document-service/contractor-company/update`,
    getContractorDocById: `document-service/contractor-company/get-by-id/`,
    approveCompanyEmployeeDocument: `document-service/employee/approve-document`,
    getAllEmployeeDocuments: `document-service/employee/get-all/by-user-id/`,
    setEmployeeDocValue: `document-service/employee/set-comment`,
    createEmployeeDocValue: `document-service/employee/create`,
    uploadUserDocument: `document-service/external/create-to-external`,
    approveContractorDocument: `document-service/contractor/approve-document`,
    approveExternalDocument: `document-service/supplier/approve-document`,
    getUserDocumentsEmployee: `document-service/employee/get-all/by-user-id/`,
    createToExternalEmployee: `document-service/employee/create-to-employee`,
    createToExternal: `document-service/external/create-to-external`,
    setToExternal: `document-service/external/set-comment`,
    getUserDocuments: `document-service/supplier/get-all/by-user-id/`,
    getAllSupplierDocumentsById: `document-service/supplier/get-all/by-user-id/`,
    createSupplierDocValue: `document-service/supplier/create`,
    setSupplierDocValue: `document-service/supplier/set-comment`,
    getDocumentRestrictionsingle: `document-service/restriction/get`,
    updateDocumentRestriction: `document-service/restriction/update`,
    getAllEmployeesDocuments: `document-service/employee/get-all/for-company`,
    getAllExternalDocuments: `document-service/external/get-all/for-company`,
};
export const CARD_SERVICE = {
    createResourceApi: `card-service/resource/create`,
    updateResourceApi: `card-service/resource/update`,
    createSingleCard: `card-service/v1/create`,
    createMultipleCards: `card-service/v1/create-all`,
    analizeCards: `card-service/v1/analyze-cards`,
    getAllCardFormats: `card-service/format/get-all`,
    getPreview: `card-service/v1/preview-by-id/`,
    assignCardToUser: `card-service/v1/user/assign-card`,
    removeCardToUser: `card-service/v1/user/remove-card`,
    createTemplateCard: `card-service/template/create`,
    updateTemplateCard: `card-service/template/update`,
    assignTemplateToCards: `card-service/template/assign-to-cards`,
    removeTemplates: `card-service/template/remove-templates-to-cards`,
};

export const STATUS_SERVICE = {
    getListStatus: `status-service/get-all-to-company`,
    userInfoStatus: `status-service/get-all-to-user`,
    getVehicleStatus: `status-service/get-all-to-vehicle`,
    getAllToContractor: `status-service/get-all-to-contractor`,
    getProviderVehicleStatus: `status-service/get-all-to-vehicle`,
    getVehicleTags: `status-service/get-all-to-tags`,
    getListStatusZone: `status-service/get-all-to-zone`,
};

export const EMPLOYEE_SERVICE = {
    getEmployeesPageable: `employee-service/get-all-pageable/only-user-data`,
    getUserByIdAtEntry: `employee-service/v1/app/get-by-user-id`,
    getSingleEmployeeDetail: `employee-service/get-by-user-id/with-selfie/`,
    getSingleEmployeeWithId: `employee-service/get-by-user-id/`,
    updateUserEmployee: `employee-service/update`,
    getAllEmployees: `employee-service/get-all-pageable/employee-data`,
    createEmployeeUser: `employee-service/v1/create`,
    getEmployeeDetailsByUserId: `employee-service/v1/get-by-user-id/`,
    updateEmployeeUser: `employee-service/v1/update`,
    getAllEmployee: `employee-service/get-all/only-user-data`,
    createListEmployee: `employee-service/create-list`,
    // The guards keep an employee record, they are created and updated through their own endpoints
    createGuard: `employee-service/guard/v1/create`,
    updateGuard: `employee-service/guard/v1/update`,
};

export const ROLE_SERVICE = {
    roleCheck: `role-service/role-task/get-all/by-role-id/`,
    getRoles: `role-service/get-all`,
    getAllroleEmployeesPageable: `role-service/get-all-employees-pageable/by-id/`,
    getEmployeeRoles: `role-service/get-all-pageable`,
    getEmployesWithRole: `role-service/get-all-employees/by-id/`,
    removeRoleToUserByIds: `role-service/remove-role-to-users-by-ids`,
    addUsersToRole: `role-service/add-role-to-users-by-ids`,
    creatRole: `role-service/v1/create`,
    addPermissionTask: `role-service/add-role-task-list`,
    updateRoleRestriction: `role-service/role-restriction/update`,
    getSingleRole: `role-service/v1/get-by-id/`,
    updateRole: `role-service/v1/update`,
};

export const DEPARTMENT_SERVICE = {
    getDepartments: `department-service/get-all`,
    getAllDepartments: `department-service/get-all-pageable`,
    createDepartment: `department-service/v1/create`,
    updateDepartment: `department-service/update`,
    deleteDepartment: `department-service/delete-by-id/`,
    addDepartmentById: (id) => `department-service/add-department-by-id/${id}/to-user-list`,
    removeAlluserFromDepartment: `department-service/remove-all-users/to-department-by-id/`,
    getUsersDepartment: `department-service/get-all-pageable-user/by-department-id/`,
    listUsersDepartment: `department-service/get-all-user/by-department-id/`,
    removeUserFromDepartment: `department-service/remove/to-user-list`,
    getAllDepartmentsNoPageable: `department-service/get-all`,
};

export const BUILDING_SERVICE = {
    getAllBuildings: `building-service/get-all-pageable`,
    getPublicBuildingById: `building-service/public/v1/building/get/`,
    createBuilding: `building-service/v1/building/create`,
    updateBuilding: `building-service/v1/building/update`,
    createResidentWhiteList: `building-service/v1/resident/white-list/create-all`,
    uploadResidentWhiteList: `building-service/v1/resident/white-list/upload`,
    createResident: `building-service/v1/resident/create`,
    publicValidateResidentOwner: `building-service/public/v1/resident/validate-owner`,
    getBuildingUserAssigned: `building-service/v1/building-user/simple/assigned`,
    getBuildingUserNotAssigned: `building-service/v1/building-user/simple`,
    createBuildingUsers: `building-service/v1/building-user/create-all`,
    deleteBuilding: `building-service/delete-by-id/`,
    getBuildingApartmentById: `building-service/v1/building-apartment/get/`,
    getPublicBuildingApartmentDetails: `building-service/public/v1/building-apartment/details/`,
};

export const WORK_SHIFT_SERVICE = {
    addWorkShift: (workShiftId, userId) =>
        `work-shift-service/work/create/by-work-shift-id/${workShiftId}/by-user-id/${userId}`,
    addCustomWorkShift: `work-shift-service/work/create-list`,
    createWorkShiftAccess: `work-shift-service/schedule/create-list`,
    getAllWorkShifts: `work-shift-service/get-all-pageable`,
    userWorkSchedule: `work-shift-service/work/get-all-pageable/by-work-shift-id/`,
    getAllByWorkShiftId: `work-shift-service/work/get-all/by-work-shift-id/`,
    addUserWithWorkShiftId: `work-shift-service/work/create-all/by-work-shift-id/`,
    deleteUserFromWorkShift: `work-shift-service/work/remove/work-shift-list/to-user-ids`,
    deleteContractFromWorkShift: `/work-shift-service/contractor-work/remove/work-shift-list/to-contractor-ids`,
    deleteWorkSHiftTime: `work-shift-service/contractor-work/delete-by-id/`,
    updateWorkShiftName: `work-shift-service/update-name`,
    createCompanyWorkShift: `work-shift-service/create`,
    createContractWithCustom: `work-shift-service/contract-work/create-list`,
    createContractWorkSchedule: (workShiftId, contractId) =>
        `work-shift-service/contract-work/create/by-workshift-id/${workShiftId}/by-contract-id/${contractId}`,
    getAllWorkSchdule: `work-shift-service/get-all`,
    getWorkTimeAccess: `work-shift-service/schedule/get-all-pageable/by-work-shift-id/`,
    getWorkShiftByContractID: `work-shift-service/contract-work/get-all-pageable/by-contract-id/`,
    getAllZone: `work-shift-service/get-all/allowed-zones`,
    getWorkShiftResourceAssigned: `work-shift-service/v1/resource-mode/simple/assigned`,
    getWorkShiftResourceNotAssigned: `work-shift-service/v1/resource-mode/simple/`,
    createWorkShiftFull: `work-shift-service/create-with-resources`,
    updateWorkShiftFull: `work-shift-service/update-with-resources`,
    getWorkShiftDetailById: `work-shift-service/get-detail/by-id/`,
};

export const IMAGE_SERVICE = {
    deleteDocumentById: (id, option) => `image-service/delete-by-id/${id}/option/${option}`,
    uploadImage: `image-service/upload`,
    downloadProviderImage: (id, option) => `image-service/download-by-id/${id}/option/${option}`,
    publicUploadImage: `image-service/public-upload`,
};
export const VEHICLE_COMPANY_SERVICE = {
    employeeVehicles: `vehicle-company-service/get-all/by-user-id/`,
    updateCompanyVehicle: `vehicle-company-service/update`,
    singleVehicleDetail: `vehicle-company-service/get-by-vehicle-id/`,
    getAllVehiclesData: `vehicle-company-service/get-all-only-vehicle-data/by-company-id`,
    getAllVehicle: `vehicle-company-service/get-all-pageable/by-company-id`,
    getAllDriverRelationship: `vehicle-company-service/user-permission/get-all/by-vehicle-id/`,
    premmissionType: `vehicle-company-service/permission-type/get-all`,
    permissionByUser: `vehicle-company-service/user-permission/get-all/by-user-id/`,
    addDriver: `vehicle-company-service/user-permission/create`,
    removeDriverEmployee: `vehicle-company-service/user-permission/delete-by-id/`,
    updateDriver: `vehicle-company-service/user-permission/update`,
};
export const FIREARM_SERVICE = {
    checkFireArms: `firearm-service/check-fire-arm/by-user-id/`,
    creatFireArms: `firearm-service/create`,
    updateFireArms: `firearm-service/update`,
    getFireArm: `firearm-service/get-by-id/`,
};
export const VEHICLE_SERVICE = {
    createVehicle: `vehicle-service/create-for-company`,
    updateVehicle: `vehicle-service/update`,
    imagesByVehicleId: `vehicle-service/vehicle-image/get-all-pageable/by-vehicle-id/`,
    createVehicleImgObject: `vehicle-service/vehicle-image/create`,
    createVehicleAndRelationContractor: `vehicle-service/create-for-contractor/`,
    getVehicleImgUsingId: `vehicle-service/vehicle-image/get-image/get-by-vehicle-id/`,
    createListContractorVehicle: `/vehicle-service/v1/vehicles/contractor`,
    createListSupplierVehicle: `/vehicle-service/v1/vehicles/supplier`,
    createListEmployeeVehicle: `/vehicle-service/v1/vehicles/employee`,
    createVehicleAndRelationSupplier: `vehicle-service/create-for-supplier/`,
    uploadProviderVehicleImage: `vehicle-service/vehicle-image/create`,
    getSingleProviderVehicle: `vehicle-service/get-by-id/`,
    updateProviderVehicleData: `vehicle-service/update`,
    createVehicleEmployee: `vehicle-service/create-for-company`,
    createVehicleImage: `vehicle-service/vehicle-image/create`,
    getVehicleById: `vehicle-service/get-by-id/`,
    updateVehicleV1: `vehicle-service/v1/update`,
    getListOfVehicleImages: `vehicle-service/vehicle-image/get-all-pageable/by-vehicle-id/`,
};
export const EXTRA_DATA_SERVICE = {
    updateExtraData: `extra-data-service/update-by-user-id/`,
    updateUserExtraData: `extra-data-service/update-by-user-id/`,
    getExtraDataService: `extra-data-service/header/get`,
    getHeaders: `extra-data-service/header/get`,
    updateHeaders: `extra-data-service/header/update`,
    getUserExtraData: `extra-data-service/get-by-user-id/`,
    updateProviderExtraData: `extra-data-service/update-by-user-id/`,
};
export const CONTRACTOR_VEHICLE_SERVICE = {
    getAllVehicleByContractor: `contractor-vehicle-service/get-all/by-contractor-id/`,
    deleteVehicleFromContract: `contractor-vehicle-service/contract/delete-by-contract-id/`,
    deleteVehicleWorkOnContract: (contractId, vehicleId) =>
        `contractor-vehicle-service/contract/delete-by-contract-id/${contractId}/by-vehicle-id/${vehicleId}`,
    getVehicleOnContractById: `contractor-vehicle-service/contract/get-all-pageable/by-contract-id/`,
    deleteVehicleByContractAndVehicleList: (contractId) =>
        `contractor-vehicle-service/contract/delete-by-contract-id/${contractId}/by-vehicle-list`,
    vehicleWorkOnContract: `contractor-vehicle-service/contract/get-all/by-contract-id/`,
    getVehicleDetailById: `contractor-vehicle-service/company/get-by-vehicle-id/`,
    getAllVehicleByContractorId: `contractor-vehicle-service/get-all/by-contractor-id/`,
    addVehicleWithContractId: `contractor-vehicle-service/contract/create-list`,
    getAllVehicleByContractorIdPageable: `contractor-vehicle-service/get-all-pageable/company/by-contractor-id/`,
    getVehicleWithDocumentById: `contractor-vehicle-service/company/get-by-vehicle-id/`,
};
export const DEVICE_SERVICE = {
    getAntiPassBackType: `device-service/anti-pass-back-type/get-all`,
    getEscortMode: `device-service/escort-mode/get-all`,
    getAllDevicesZoneNull: `device-service/get-all/by-zone-id-null`,
    getAllDevices: `device-service/get-all`,
    createDeviceApi: `device-service/create`,
    createAirbnkLock: `device-service/airbnk-lock/create`,
    getDeviceSmartlock: `device-service/smart-lock/get-by-device-id/`,
    getDeviceAirbnkLock: `device-service/airbnk-lock/get-by-device-id/`,
    getDeviceDetails: `device-service/get-by-id/`,
    getDeviceLog: `device-service/log/get-all/by-device-id/`,
    getQrCode: `device-service/v1/get-qr-code/`,
    getDeviceLogPageable: `device-service/log/get-all-pageable/by-device-id/`,
    addFreeDevice: (id, zoneId) => `device-service/add-device/by-id/${id}/to-zone-id/${zoneId}`,
    removeDevicesZone: `device-service/remove-zone/by-device-list`,
    updateDeviceApi: `device-service/v1/app/update`,
    updateAirbnk: `device-service/airbnk-lock/update`,
    getAllDevicesByModeIds: `device-service/device-mode/get-by-ids`,
    getAccessType: `device-service/device-access-type/get-all`,
    getDeviceType: `device-service/device-type/get-all`,
    createDeviceZone: `device-service/create`,
    createDeviceSmartlock: `device-service/smart-lock/create`,
    getDeviceZone: `device-service/get-by-id/`,
    deleteDevice: `device-service/remove-device/by-id/`,
    updateDeviceSmartlock: `device-service/smart-lock/update`,
    setZoneImageCoordinate: `device-service/zone-plane/set-coordinates`,
    getZoneDevicesLists: `device-service/zone-plane/get-all/by-zone-plane-id/`,
    updateDeviceZone: `device-service/update`,
    simpleModeSearch: 'device-service/v1/simple-mode-search',
    getAssigned : '/device-service/v1/authentication-mode/simple/assigned',
    getNoAssigned : '/device-service/v1/authentication-mode/simple/',
    createEmployee :'/device-service/v1/authentication-mode/user/create',
    createDepartment :'/device-service/v1/authentication-mode/department/create',
    openDoorByDeviceOtp :'/device-service/v2/web/open-the-door/by-device-id/'
};
export const DISTRIBUTION_CENTER_SERVICE = {
    getAllDistributionCenter: `distribution-center-service/v1/get-all`,
    createDistributionCenter: `distribution-center-service/v1/create`,
    updateDistributionCenter: `distribution-center-service/v1/update`,
    deleteDistributionCenter: `distribution-center-service/delete-by-id/`,
    listOfUsersDistributionCenter: `distribution-center-service/get-all-pageable-user/by-distribution-center-id/`,
    getAllNonDistributionCenterUser: `distribution-center-service/get-all-users/who-dont-belong-to-distribution-center/by-distribution-center-id/`,
};
export const VISITOR_TYPES_SERVICE = {
    getAllVisitorTypes: `visitor-types-service/get-all-active-visitor-types`,
    getVisitorTypeByID: `visitor-types-service/get-by-id/`,
    createVisitorTypes: `visitor-types-service/create`,
    updateVisitorTypes: `visitor-types-service/update`,
    getVisitorTypeById: `visitor-types-service/get-by-id`,
};
export const ONBOARDING_SERVICE = {
    getOnBoarding: `onboarding-service/get-all-pageable`,
    getOnBoardingById: `onboarding-service/get-by-id/`,
    createOnBoarding: `onboarding-service/create`,
    getAllOnBoardingProcess: `onboarding-service/process/get-all/by-onboarding-id/`,
    createOnBoardingProcess: `onboarding-service/process/create`,
    createListWithProcess: `onboarding-service/process/create-list`,
    deleteOnboardingProcess: `onboarding-service/process/delete-by-id/`,
    updateOnBoarding: `onboarding-service/update`,
};
export const ORDER_SERVICE = {
    createSupplierOrder: `order-service/v1/create`,
    getSupplierOrder: `order-service/get-by-id/`,
    completeOrderProvider: `order-service/complete-order/`,
    cancelSupplierOrder: 'order-service/cancel/',
    markOrderAsDelivered: 'order-service/deliver',
    employeeFlowUpdateOrder: 'order-service/update',
    getOrderDetailsV1: 'order-service/v1/get-by-id/',
};

export const TRAFFIC_FINE_SERVICE = {
    updateGeofence: `traffic-fine-service/v1/geofences/update`,
    getGeofenceById: `traffic-fine-service/v1/geofences/`,
    getGeofenceList: `traffic-fine-service/v1/geofences/get-all`,
    updateGeofenceHierarchyLevel: `traffic-fine-service/v1/geofences/update-hierarchy-Level`,
    updateGeofenceStatus: `traffic-fine-service/v1/geofences/update-status`,
    getGeofenceConfigSettingByGeofenceId: `traffic-fine-service/v1/geofence-config/geofence-id/`,
    createGeofence: `traffic-fine-service/v1/geofences/create`,
    updateFineSettings: `traffic-fine-service/v1/settings/update`,
    getFineSettingsById: `traffic-fine-service/v1/settings/1`,
    createFineType: `traffic-fine-service/v1/types/create`,
    updateFineType: `traffic-fine-service/v1/types/update`,
    getUserTrafficDetails: `traffic-fine-service/v1/user/`,
    getFinesTrafficDetails: `traffic-fine-service/v1/`,
    getEvidencesList: `traffic-fine-service/v1/evidences/`,
    getAllGeofenceList: `traffic-fine-service/v1/geofences/get-all`,
    registerFinePaid: `traffic-fine-service/v1/payments/update`,
    createPaymentEvidence: `traffic-fine-service/v1/payments-evidence/create`,
    getPaymentEvidence: `traffic-fine-service/v1/payments-evidence/fine-payment-id/`,
    getAllLocationsByUserAndByDates: `traffic-fine-service/v1/get/locations`,
    getAllFineByDates: (from, to) => `traffic-fine-service/v1/get-all/from/${from}/to/${to}`,
    createFine: `traffic-fine-service/v1/create`,
    createEvidencesFine: `traffic-fine-service/v1/evidences/create`,
    getAllFineTypes: `traffic-fine-service/v1/types/get-all`,
    getGeofenceConfigSetting: `traffic-fine-service/v1/geofence-config/get`,
};

export const FOODIE_ACCESS_SERVICE = {
    getAccessCodeById: `foodie-access-service/v1/by-id/`,
    createAccessCode: `foodie-access-service/v1/create`,
    getAccessCodeQR: `foodie-access-service/v1/qr-code/`,
    cancelAccessCode: `foodie-access-service/v1/cancel`,
};
export const CHART_SERVICE = {
    getGraphByUserId: `chart-service/v1/dashboard/get-all/by-user-id/`,
    getTemplates: `chart-service/v1/template/get-all`,
    getAllChartsAvailable: `chart-service/v1/get-all`,
    getChartsAvailableByDashboardId: (dashboardId) => `chart-service/v1/chart-dashboard/${dashboardId}/non-charts`,
    addCharts: `chart-service/v1/chart-dashboard/add-charts`,
    updateDashboardName: `chart-service/v1/dashboard/update`,
    updateChartPosition: `chart-service/v1/chart-dashboard/update/chart-positions`,
    createDashboard: `chart-service/v1/dashboard/create`,
};

export const REQUEST_ACCESS_SERVICE = {
    createRequestAccess: `request-access-service/v1/create`,
    getRequestAccessById: `request-access-service/v1/get-by-id/`,
    getRequestAccessAssets: `request-access-service/v1/request-access-asset/`,
    applyDecisionRequest: `request-access-service/v1/apply-decision`,
    assignEmployeeRequest: `request-access-service/v1/assign-employee`,
    publicCreateRequest: `request-access-service/v1/public/create`,
    createRequestDocument: `request-access-service/v1/public/request-access-asset/create`,
    markFilesAsRead: `request-access-service/v1/notifications/mark-as-read`,
};

export const SERVICE_TYPE = {
    getAllServiceType: `service-type/v1/get-all`,
    createServiceType: `service-type/v1/create`,
    updateServiceType: `service-type/v1/update`,
    deleteServiceType: `service-type/delete-by-id/`,
    listOfUsersServiceType: `service-type/get-all-pageable-user/by-service-type-id/`,
    getAllNonServiceType: `service-type/get-all-users/who-dont-belong-to-service-type/by-service-type-id/`,
};
export const SYSTEM_CONFIG_SERVICE = {
    getFirstSystemConfig: `system-config-service/v1/get`,
    partialUpdateSystemConfig: `system-config-service/v1/partial-update`,
    getConfigByName: `system-config-service/v1/get/`,
    getTolerances: `system-config-service/v1/tolerances`,
};
export const COMPANY_SERVICE = {
    getUserCompanyRestrictionData: `company-service/company-restriction`,
    companyDataToUpdate: `company-service/get`,
    updateCompany: `company-service/v1/app/update`,
};
export const VALIDAS_SERVICE = {
    getDocumentValidationStatus: `validas-service/v1/process`,
    validateObverseDocument: `validas-service/v2/validation/obverse-document`,
    validateReverseDocument: `validas-service/v2/validation/reverse-document`,
    veridasServiceProcess: `validas-service/v2/process`,
    validateVideoPhoto: `validas-service/v2/validation/video-photo`,
};


export const FILE_SERVICE = {
    createAttendanceExcelReport: `file-service/attendance/excel`,
    createAttendancePDFReport: `file-service/attendance/pdf`,
    createSimpleAttendanceExcelReport: `file-service/attendance/simple-excel`,
    createSimpleAttendancePDFReport: `file-service/attendance/simple-pdf`,
    exportPDFSimple: `file-service/simple-pdf/`,
    exportPDFSimpleCustom: `file-service/simple-pdf/custom/`,
    exportExcelSimple: `file-service/simple-excel/`,
    exportExcelSimpleCustom: `file-service/simple-excel/custom/`,
    getExcelHeadersByOption: `file-service/headers/`,
    exportDataPDF: (option, moduleId) => `file-service/${option}/${moduleId}/pdf`,
    exportDataPDFCustom: (option, moduleId) => `file-service/${option}/${moduleId}/pdf/custom`,
    exportDataExcel: (option, moduleId) => `file-service/${option}/${moduleId}/excel`,
    exportDataExcelCustom: (option, moduleId) => `file-service/${option}/${moduleId}/excel/custom`,
    downloadEmployeeForm: `file-service/download/employee`,
    uploadEmployeeFormFile: `file-service/upload/employees`,
    downloadContractorEmployeeForm: `file-service/download/contractor-employee`,
    uploadContractorEmployeeFormFile: `file-service/upload/contractor-employees`,
    downloadSupplierEmployeeForm: `file-service/download/supplier-employee`,
    uploadSupplierEmployeeFormFile: `file-service/upload/supplier-employees`,
    downloadSupplierForm: `file-service/download/supplier`,
    uploadSupplierFormFile: `file-service/upload/suppliers`,
    downloadContractorForm: `file-service/download/contractor`,
    uploadContractorFormFile: `file-service/upload/contractors`,
    downloadContractorVehicleForm: `file-service/download/contractor-vehicle`,
    uploadContractorVehicleFormFile: `file-service/upload/contractor-vehicles`,
    downloadSupplierVehicleForm: `file-service/download/supplier-vehicle`,
    uploadSupplierVehicleFormFile: `file-service/upload/supplier-vehicles`,
    downloadEmployeeVehicleForm: `file-service/download/employee-vehicle`,
    uploadEmployeeVehicleFormFile: `file-service/upload/employee-vehicles`,
    downloadReportByName: `file-service/download/report/`,
    pilgrimsReports: (exportFormat) =>`file-service/${exportFormat}/pilgrims-service`
};

export const FOODIE_SERVICE = {
    getColorFoodie: `foodie-service/v1/colors/get`,
    getSecurityLogs: `foodie-service/v1/logs/user/`,
    getFirstFoodieSetting: `foodie-service/v1/settings/get`,
    getAllFoodieDepartment: `foodie-service/v1/departments/get-all`,
    getFoodieDepartmentById: `foodie-service/v1/departments/by-id/`,
    getAllFoodieDevices: `foodie-service/v1/devices/get-all`,
    getFoodieRecordById: `foodie-service/v1/`,
    createsFoodieRecord: `foodie-service/v1/create`,
    createsFoodieDepartment: `foodie-service/v1/departments/create`,
    addDeviceToListFoodie: `foodie-service/v1/devices/create/by-device-id/`,
    updateFoodieDepartment: `foodie-service/v1/departments/update`,
    updateFoodieSettingColor: `foodie-service/v1/colors/update`,
    removeDeviceFoodie: `foodie-service/v1/devices/delete-list`,
    updateSettingsRestrictions: `foodie-service/v1/settings/update`,
    updateFoodieRecordById: `foodie-service/v1/update`,
};

export const INVITATION_SERVICE = {
    submitInvitation: `invitation-service/submit-fields/`,
    getInvitationById: `invitation-service/details-with-fields/`,
    getInvitationHeadersByOption: `invitation-service/headers/`,
    getInvitationValuesByOptionFieldAndUserId: (field, userId, viewType) =>
        `invitation-service/${field}/values-to-filter/to-user-id/${userId}/${viewType}`,
    createInvitation: `invitation-service/v1/web/create`,
    getAllVisitors: `invitation-service/v1/get-all-visitors`,
    deleteInvitation: `invitation-service/delete-by-id/`,
    countAllByAllStatus: `invitation-service/count-all/by-all-status/`,
    notifyHost: `invitation-service/notify-host/`,
    markFilesAsRead: `invitation-service/v1/notifications/mark-as-read`,
    updateVisitorStatusByInvitationId: (invitationId, statusId) =>
        `invitation-service/validate-visitor-invitation/${invitationId}/option/${statusId}`,
    updateStatusByInvitationId: (invitationId, option, statusReason) =>
        `invitation-service/validate-invitation/${invitationId}/option/${option}/params/${statusReason}`,
    publicDeleteItemsById: `invitation-service/public/delete-by-ids`,
    publicUpdateStatusByInvitationId: (invitationId, option, statusReason) =>
        `invitation-service/public/validate-invitation/${invitationId}/option/${option}/params/${statusReason}`,
    publicUpdateVisitorStatusByInvitationId: (invitationId, statusId) =>
        `invitation-service/public/validate-visitor-invitation/${invitationId}/option/${statusId}`,
    publicGenerateICSByInvitation: (invitationId, action) =>
        `invitation-service/public/generate-ics/${invitationId}/ics-action/${action}`,

    getSearchUserServiceByEmail : 'user-service/get-by-email',
    getSearchUserServiceByPhone : 'user-service/get-by-phone-number'
};

export const LOGISTIC_REQUEST_SERVICE = {
    getAllLogisticRequestById: `logistic-request-service/v1/`,
    getAllLogisticRequest: `logistic-request-service/v1/get-all`,
    createLogisticRequest: `logistic-request-service/v1/create`,
    updateLogisticRequest: `logistic-request-service/v1/update/`,
    deleteLogisticRequest: `logistic-request-service/get-all-pageable-user/by-logistic-request-service-id/`,
    assignLogisticRequest: `logistic-request-service/v1/assign/`,
    acceptLogisticRequest: `logistic-request-service/v1/accept/`,
    rejectLogisticRequest: `logistic-request-service/v1/reject/`,
    preInTransitIssueLogisticRequest: `logistic-request-service/v1/pre-in-transit-issue`,
    inTransitIssueLogisticRequest: `logistic-request-service/v1/in-transit-issue/`,
    inTransitLogisticRequest: `logistic-request-service/v1/in-transit-issue/`,
    deliverLogisticRequest: `logistic-request-service/v1/deliver/`,
    cancelLogisticRequest: `logistic-request-service/v1/cancel/`,
    addEmployeesVehiclesLogisticRequest: `logistic-request-service/v1/add-employees-vehicles/`,
};

export const ADVANCED_SEARCH_SERVICE = {
    getAttributesFilter: `advanced-search-service/get-all/by-module-id/`,
    getConditionFilter: `advanced-search-service/get-operation-keys/by-type/`,
    getValuesByOptionAndField: (option, field) => `advanced-search-service/${option}/${field}/values-to-filter`,
    getValuesByOptionFieldAndUserId: (option, field, userId) => `advanced-search-service/${option}/${field}/values-to-filter/to-user-id/${userId}`,
    searchByFilters: `advanced-search-service/`,
    searchByFiltersCustom: (option) => `advanced-search-service/${option}/custom`,
    getListSearchModels: `advanced-search-service/models`,
    simpleSearch: `advanced-search-service/simple`,
    getValuesToFilterLogByField: (model, field) => `advanced-search-service/crud-log/${model}/${field}/values`,
    searchCrudLogByModel: (model) => `advanced-search-service/${model}/crud-log`,
    simpleSearchWithId: `advanced-search-service/simple/user`,
    getAllInvitation: `advanced-search-service/simple`,
    searchUserZone: (model) => `advanced-search-service/${model}/user-zone`,
};


export const AUTHENTICATION_SERVICE = {
    publicProcessCompleted: `authentication-service/public/v1/process-completed/`,
    sendEmailToResetPassword: (username) => `authentication-service/send-email/reset-password/${username}`,
    verifyOtp: 'authentication-service/verify-otp',
    newPassword: 'authentication-service/new-password',
    preRegister: 'authentication-service/v4/pre-register',
};

export const ACCESS_MONITORING_SERVICE = {
    publishDailyAccessMonitoring : "/app/daily-access-monitoring",
    topicDailyAccessMonitoring : "/topic/daily-access-monitoring"
}

export const USER_SPECIAL_PERMISSION_SERVICE = {
    createMultipleSpecialPermission: "user-special-permission-service/v1/create",
    getSpecialPermissionAssigned: "user-special-permission-service/v1/simple/assigned",
    getSpecialPermissionNotAssigned: "user-special-permission-service/v1/simple",
    countSpecialPermissionAssigned: "user-special-permission-service/v1/count/assigned",
}

export const ATTENDANCE_ADJUSTMENT_SERVICE = {
    getAttendancePeriodActive: "attendance-adjustment-service/attendance-period/active",
    getAttendancePeriodReview: "attendance-adjustment-service/attendance-period/review",
    getAttendancePeriodRequest: "attendance-adjustment-service/attendance-period/request",
    getAttendanceAdjustmentGroupById: "attendance-adjustment-service/group/",
    getAttendanceAdjustmentGroupPendingList: "attendance-adjustment-service/group/pending-list",
    attendanceAdjustmentApprovalAction: "attendance-adjustment-service/group/approval-action",
    saveAttendanceAdjustment: "attendance-adjustment-service/group/create",
    validateAttendanceAdjustmentDate: "attendance-adjustment-service/group/validate-date",
    adjustAttendanceSubmissionGroup: "attendance-adjustment-service/group/adjust",
    adjustAttendanceChangeRequestSubmission: "attendance-adjustment-service/change-request/adjust"
}

export const PAYROLL_POLICY_SERVICE = {
    getPayrollPolicy: "payroll-policy-service/v1/get-by-id/",
    getPayrollPolicyByGroupId:  "payroll-policy-service/v1/group/get-by-id/",
    updatePayrollPolicy: "payroll-policy-service/v1/update",
    updateStatusPayrollPolicy: "payroll-policy-service/v1/update-status",
    updatePriorityPayrollPolicy: "payroll-policy-service/v1/update-priority"
}

export const PAYROLL_DEDUCTION_POLICY_SERVICE = {
    getAll:   "payroll-deduction-policy-service/v1/get-all",
    getById:  "payroll-deduction-policy-service/v1/get-by-id/",
    create:   "payroll-deduction-policy-service/v1/create",
    update:   "payroll-deduction-policy-service/v1/update",
    delete:   "payroll-deduction-policy-service/v1/delete",
    createUserDeductionPolicy: "payroll-deduction-policy-service/v1/user/create",
    updateUserDeductionPolicy: "payroll-deduction-policy-service/v1/user/update",
    getUserDeductionPolicy: 'payroll-deduction-policy-service/v1/user/'
}

export const PAYROLL_POLICY_GROUP_SERVICE = {
    getPayrollPolicyGroup: "payroll-policy-group-service/v1/get-by-id/",
    createPayrollPolicyGroup: "payroll-policy-group-service/v1/create",
    updatePayrollPolicyGroup: "payroll-policy-group-service/v1/update",
    updateStatusPayrollPolicyGroup: "payroll-policy-group-service/v1/update-status",
    getOvertimePolicyGroupConfig: "payroll-policy-group-service/v1/global/conflict-resolution",
    updateOvertimePolicyGroupConfig: "payroll-policy-group-service/v1/global/update-conflict-resolution"
}

export const PAYROLL_RESULT_SERVICE = {
    getPayrollResultById: "payroll-result-service/v1/get-by-id/",
    getOvertimeByPayrollResultId: "payroll-result-service/v1/overtime/assigned/",
    getDeductionByPayrollResultId: "payroll-result-service/v1/deduction/assigned/",
    updatePayrollUser: "payroll-result-service/v1/calculate/period",
    getPayrollResultPeriodYears:"payroll-result-service/v1/period/years",
    getFinishedPayrollResultPeriodsByYear:"payroll-result-service/v1/period/list/"
}

export const ENDECRYPT_SERVICE = {
    decrypt: `endecrypt-service/decrypt/`,
};

export const MATERIAL_REQUEST_SERVICE = {
    getMaterialRequestById: `material-request-service/v1/`,
    createMaterialRequest: `material-request-service/v1/create`,
    approvalMaterialRequest: `material-request-service/v1/approval-action`,
    dispatchMaterialRequest: `material-request-service/v1/dispatch-action`,
    createMaterialRequestItemEvidence: `material-request-service/v1/create/item-evidence`,
    updateMaterialRequestItemEvidence: `material-request-service/v1/update/item-evidence`,
    getMaterialRequestQrCode: `material-request-service/v1/qr-code/`,
}

export const MATERIAL_CONTROL_SERVICE = {
    getMaterialControlById: `material-control-service/v1/`,
    createMaterialControl: `material-control-service/v1/create`,
    approvalAction: `material-control-service/v1/approval-action`,
    registerGateEvent: `material-control-service/v1/gate-event`,
    validateItemComplete: `material-control-service/v1/item-complete/validate`,
    registerItemsBasic: `material-control-service/v1/item-basic/`,
    registerActualQuantities: `material-control-service/v1/packaging-quantity`,
    registerTrailers: `material-control-service/v1/trailer`,
    updateTransport: `material-control-service/v1/transport`,
    updateMovementValues: `material-control-service/v1/movement-values`,
    createReport: `material-control-service/v1/report`,
    getMaterialControlQrCode: `material-control-service/v1/qr-code/`,
}

export const ZONE_ACCESS_SERVICE = {
    getAll: `zone-access-service/v1/`,
    getById: `zone-access-service/v1/`,
    getByGuard: `zone-access-service/v1/by-guard/`,
    create: `zone-access-service/v1/create`,
    update: `zone-access-service/v1/update/`,
    assignGuard: `zone-access-service/v1/assign-guard`,
}

export const EVENT_MONITORING_SERVICE = {
    getActiveLayout: `event-monitoring-service/v1/layout/active`,
    createLayout: `event-monitoring-service/v1/layout/create`,
    updateLayout: `event-monitoring-service/v1/layout/update`,
    setActiveLayout: `event-monitoring-service/v1/layout/active/`,
    deleteLayout: `event-monitoring-service/v1/layout/delete/`,
    setWalkthroughDone: `event-monitoring-service/v1/walkthrough/done`,
    createPanel: `event-monitoring-service/v1/panel/create`,
    updatePanel: `event-monitoring-service/v1/panel/update`,
    updatePanelsGeometry: `event-monitoring-service/v1/panel/geometry/update`,
    deletePanel: `event-monitoring-service/v1/panel/delete/`,
    getPanelEvents: (panelId, size, before, since) => `event-monitoring-service/v1/event/panel/${panelId}?size=${size}${before ? `&before=${before}` : ''}${since ? `&since=${since}` : ''}`,
    getPanelAreas: (panelId, since) => `event-monitoring-service/v1/event/panel/${panelId}/areas${since ? `?since=${since}` : ''}`,
    getPanelAreaEvents: (panelId, areaId, size, before, since) => `event-monitoring-service/v1/event/panel/${panelId}/areas/${areaId}?size=${size}${before ? `&before=${before}` : ''}${since ? `&since=${since}` : ''}`,
    getReports: (size, before) => `event-monitoring-service/v1/event/report?size=${size}${before ? `&before=${before}` : ''}`,
    topicEvents: '/topic/event-monitoring',
}
